import { z } from 'zod';
import { normalizedUuidSchema, uuidSchema } from './common';
import { designDocumentSchema, designDocumentShape, MAX_DESIGN_NODES } from './design';
import { facePhotoSizeSchema } from './image-asset';

// ---------------------------------------------------------------------------
// 고객 디자인 저장·조회 API 입출력(결정 D7) — apps/api/src/modules/designs 의 designs.create·update·get·
// assets.resolveUrls 가 쓴다. 문서 모양·참조 수집·거부 문구는 design.ts, 이미지 업로드 계약은 design-asset.ts.
// 입력은 strict(낯선 키 거부), 응답은 non-strict — 라우터 .output() 이 서비스가 돌려준 여분 키를 떨어뜨린다.
// ---------------------------------------------------------------------------

/** designs.name 상한 — chk_designs_name_length(1~100) 와 같다. */
export const MAX_DESIGN_NAME_LENGTH = 100;
export const designNameSchema = z.string().trim().min(1).max(MAX_DESIGN_NAME_LENGTH);

export const createDesignInputSchema = z.strictObject({
  /** 없으면 서비스가 상품명(100자 절단)을 쓴다. */
  name: designNameSchema.optional(),
  document: designDocumentSchema,
});
export type CreateDesignInput = z.infer<typeof createDesignInputSchema>;

export const updateDesignInputSchema = z.strictObject({
  id: normalizedUuidSchema,
  /** 없으면 기존 이름을 유지한다. */
  name: designNameSchema.optional(),
  /** document.productId 가 designs.product_id 와 다르면 400(DESIGN_PRODUCT_IMMUTABLE_MESSAGE). */
  document: designDocumentSchema,
});
export type UpdateDesignInput = z.infer<typeof updateDesignInputSchema>;

/** common.ts 의 idParamSchema 는 non-strict 라 따로 둔다. */
export const getDesignInputSchema = z.strictObject({ id: normalizedUuidSchema });
export type GetDesignInput = z.infer<typeof getDesignInputSchema>;

/** READY 에셋 한 건의 표시 정보 — url 은 서명 URL(VIEW_URL_EXPIRES_IN_SECONDS 동안 유효). */
export const designAssetViewSchema = z.object({
  /** design_assets.id — image 노드의 assetId 와 맞춘다. */
  id: uuidSchema,
  ...facePhotoSizeSchema.shape,
  url: z.url(),
});
export type DesignAssetView = z.infer<typeof designAssetViewSchema>;

/** designs.get 응답 — 행 컬럼(id·name·schema_version·product_id·color_option_id·시각) + body + 참조 에셋. */
export const designSchema = z.object({
  id: uuidSchema,
  name: designNameSchema,
  /** 행의 schema_version 컬럼 값. JSON 본문 안에는 없다. */
  schemaVersion: z.number().int().min(1),
  ...designDocumentShape,
  /** 문서의 image 노드가 참조하는 에셋 — assetId 당 한 건. */
  assets: z.array(designAssetViewSchema).max(MAX_DESIGN_NODES),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Design = z.infer<typeof designSchema>;

/** designs.create·update 응답. */
export const saveDesignResultSchema = z.object({
  id: uuidSchema,
  updatedAt: z.iso.datetime(),
});
export type SaveDesignResult = z.infer<typeof saveDesignResultSchema>;

/**
 * 표시 URL 갱신 한 번의 assetId 상한(결정 E3). resolveUrls 는 query(GET)라 assetIds 가 요청줄에 실린다 —
 * 100개 ≈ 4.6KB 로 nginx 기본 요청줄 버퍼(8k)·CloudFront URL 상한(8192바이트) 아래다(문서 노드 상한 200개면 ≈ 9KB 로 414).
 * 문서의 이미지 에셋이 이보다 많으면 클라이언트가 나눠 부른다 — 같은 틱에 나눠 부른 호출은 web·admin 링크(httpBatchLink)가
 * URL 이 TRPC_MAX_URL_LENGTH(6000)를 넘기 전에 GET 을 나누므로 따로 순차로 보내지 않아도 된다.
 */
export const MAX_RESOLVE_DESIGN_ASSET_IDS = 100;

/** 표시 URL 갱신 — 중복 id 는 계약이 막지 않고 서비스가 1회만 처리한다. PENDING·타인·없음이 하나라도 섞이면 전체 404. */
export const resolveDesignAssetUrlsInputSchema = z.strictObject({
  assetIds: z.array(normalizedUuidSchema).min(1).max(MAX_RESOLVE_DESIGN_ASSET_IDS),
});
export type ResolveDesignAssetUrlsInput = z.infer<typeof resolveDesignAssetUrlsInputSchema>;
export const resolveDesignAssetUrlsResultSchema = z.object({
  assets: z.array(designAssetViewSchema).max(MAX_RESOLVE_DESIGN_ASSET_IDS),
});
export type ResolveDesignAssetUrlsResult = z.infer<typeof resolveDesignAssetUrlsResultSchema>;

// ---------------------------------------------------------------------------
// designs.list — 내 디자인 목록(결정 D7, 스펙 2026-10-12-server-thumbnails-design.md §5-4).
// keyset(updated_at desc, id). 커서는 불투명 문자열(서비스가 만들고 해석한다).
// ---------------------------------------------------------------------------

export const MAX_DESIGN_LIST_LIMIT = 50;
export const DEFAULT_DESIGN_LIST_LIMIT = 20;
export const DESIGN_LIST_CURSOR_INVALID_MESSAGE = '목록 커서가 올바르지 않습니다.';

export const listDesignsInputSchema = z.strictObject({
  cursor: z.string().min(1).max(200).optional(),
  limit: z.number().int().min(1).max(MAX_DESIGN_LIST_LIMIT).optional(),
});
export type ListDesignsInput = z.infer<typeof listDesignsInputSchema>;

/** 저장본과 일치하는 썸네일. isStale 이면 편집영역 또는 렌더러 버전이 현재와 달라 재생성될 때까지 옛 그림을 보여 준다(정책 §3-6). */
export const designThumbnailViewSchema = z.object({
  url: z.url(),
  isStale: z.boolean(),
});
export type DesignThumbnailView = z.infer<typeof designThumbnailViewSchema>;

export const designListProductStatusSchema = z.enum(['DRAFT', 'PUBLISHED', 'HIDDEN']);

export const designListItemSchema = z.object({
  id: uuidSchema,
  name: designNameSchema,
  productId: uuidSchema,
  productName: z.string(),
  /** 열기는 designs.get 이 게이트한다 — 목록은 상태를 함께 주고 걸러내지 않는다(스펙 T11). */
  productStatus: designListProductStatusSchema,
  colorOptionId: uuidSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** null = 준비 중(썸네일 없음 또는 저장본과 불일치). */
  thumbnail: designThumbnailViewSchema.nullable(),
});
export type DesignListItem = z.infer<typeof designListItemSchema>;

export const listDesignsOutputSchema = z.object({
  items: z.array(designListItemSchema).max(MAX_DESIGN_LIST_LIMIT),
  nextCursor: z.string().nullable(),
});
export type ListDesignsOutput = z.infer<typeof listDesignsOutputSchema>;

// ---------------------------------------------------------------------------
// designs.delete·duplicate — 내 디자인 카드의 삭제·복제(2026-10-19 결정).
// 삭제는 바로 삭제(휴지통 없음). 견적·주문은 요청 때 본문을 복사해 두므로 진행 중이어도 막지 않는다.
// 복제는 같은 상품·색상·본문으로 새 디자인을 만들고 이미지는 참조만 더한다.
// ---------------------------------------------------------------------------

export const deleteDesignInputSchema = z.strictObject({ id: normalizedUuidSchema });
export type DeleteDesignInput = z.infer<typeof deleteDesignInputSchema>;
export const deleteDesignResultSchema = z.object({ id: uuidSchema });
export type DeleteDesignResult = z.infer<typeof deleteDesignResultSchema>;

export const duplicateDesignInputSchema = z.strictObject({ id: normalizedUuidSchema });
export type DuplicateDesignInput = z.infer<typeof duplicateDesignInputSchema>;

const DUPLICATE_SUFFIX_PATTERN = /\s*\(사본(?: \d+)?\)$/u;

/** 사본 이름의 바탕 — 이미 사본이던 이름("노트 (사본 2)")은 꼬리를 떼어 "노트"로 본다. 떼고 나서 비면 원본을 그대로 쓴다. */
export const duplicateDesignBase = (name: string): string => {
  const base = name.replace(DUPLICATE_SUFFIX_PATTERN, '').trimEnd();
  return base.length === 0 ? name : base;
};

const copyName = (base: string, n: number): string => {
  const suffix = n === 1 ? ' (사본)' : ` (사본 ${n})`;
  return `${base.slice(0, MAX_DESIGN_NAME_LENGTH - suffix.length).trimEnd()}${suffix}`;
};

/**
 * 사본 이름 — "바탕 (사본)", 그 이름이 이미 있으면 "바탕 (사본 2)", "바탕 (사본 3)"… takenNames 에 없는 첫 번호를 쓴다.
 * 상한(100자)을 넘으면 바탕 쪽을 잘라 맞춘다. takenNames 는 유한하므로 반드시 끝난다.
 */
export const duplicateDesignName = (name: string, takenNames: Iterable<string> = []): string => {
  const base = duplicateDesignBase(name);
  const taken = new Set(takenNames);
  let n = 1;
  while (taken.has(copyName(base, n))) n += 1;
  return copyName(base, n);
};

// ---------------------------------------------------------------------------
// designs.rename — 내 디자인 카드에서 이름만 바꾼다(2026-10-20). 본문·색상은 그대로이고 updated_at 도 건드리지 않는다 —
// 목록 정렬 키이자 썸네일 유효 기준이라, 이름만 바꾼 디자인이 맨 앞으로 튀거나 썸네일이 "준비 중"으로 돌아가면 안 된다.
// ---------------------------------------------------------------------------

export const renameDesignInputSchema = z.strictObject({
  id: normalizedUuidSchema,
  name: designNameSchema,
});
export type RenameDesignInput = z.infer<typeof renameDesignInputSchema>;
export const renameDesignResultSchema = z.object({ id: uuidSchema, name: designNameSchema });
export type RenameDesignResult = z.infer<typeof renameDesignResultSchema>;
